import { Trans, useTranslation } from 'react-i18next';

import { Button, Modal, Text } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { BrandsState } from '../Brands.state';
import { BrandsFormState } from '../BrandsForm';

function SingleContent({ formState = BrandsFormState }: { formState?: typeof BrandsFormState }) {
  const { t } = useTranslation('library');

  const { entityType, parentName, brandName, brandList, brandItemCounts } = useSnapshot(formState);

  const nameValue = entityType === 'parent' ? parentName : brandName;

  return (
    <div className="w-full px-8 py-4">
      <Text variant="heading-1" weight="semibold" className="mb-3">
        {t('brands.deleteModal.title.single')} {nameValue}?
      </Text>
      {entityType === 'parent' && (
        <Text>
          <Trans i18nKey="brands.deleteModal.description.parent" ns="library" count={brandList.length} />
        </Text>
      )}
      {entityType === 'brand' && (
        <Text>
          <Trans i18nKey="brands.deleteModal.description.brand" ns="library" count={brandItemCounts} />
        </Text>
      )}
    </div>
  );
}

function BulkContent({ state = BrandsState }: { state?: typeof BrandsState }) {
  const { t } = useTranslation('library');

  const { anyChildCount } = useSnapshot(state);

  return (
    <div className="w-full px-8 py-4">
      <Text variant="heading-1" weight="semibold" className="mb-3">
        {t('brands.deleteModal.title.bulk')}
      </Text>
      <Text>
        <Trans i18nKey="brands.deleteModal.description.bulk" ns="library" count={anyChildCount} />
      </Text>
    </div>
  );
}

function AllOutletContent({ bulk, formState = BrandsFormState }: { bulk?: boolean; formState?: typeof BrandsFormState }) {
  const { t } = useTranslation('library');

  const { entityType, parentName, brandName } = useSnapshot(formState);

  let nameValue;

  if (bulk) {
    nameValue = 'principal/brand';
  } else if (entityType === 'parent') {
    nameValue = parentName;
  } else {
    nameValue = brandName;
  }

  const tlKey = bulk ? 'brands.deleteModal.description.allOutlet.bulk' : `brands.deleteModal.description.allOutlet.${entityType}`;

  return (
    <div className="w-full px-8 py-4">
      <Text variant="heading-1" weight="semibold" className="mb-3">
        {t('brands.deleteModal.title.allOutlet', { subject: nameValue })}
      </Text>
      <Text>
        <Trans i18nKey={tlKey} ns="library" />
      </Text>
    </div>
  );
}

function BrandsDeleteModal({
  open,
  variant,
  isLoading,
  onToggle,
  onClickDelete,
}: {
  open: boolean;
  variant: 'single' | 'bulk' | 'all_outlet' | 'bulk_all_outlet';
  isLoading?: boolean;
  onToggle: () => void;
  onClickDelete: () => void;
}) {
  const { t } = useTranslation('library');

  return (
    <Modal unwrap open={open} onOpenChange={onToggle}>
      <Modal.Content className="w-auto !max-w-144">
        {variant === 'single' && <SingleContent />}
        {variant === 'bulk' && <BulkContent />}
        {variant === 'all_outlet' && <AllOutletContent />}
        {variant === 'bulk_all_outlet' && <AllOutletContent bulk />}
        <div className="flex w-full gap-5 px-8 py-4">
          <Modal.Trigger className="w-full">
            <Button variant="secondary" size="l" className="w-full" disabled={isLoading}>
              {t('brands.cancelButton')}
            </Button>
          </Modal.Trigger>
          <Button variant="danger" size="l" className="w-full" loading={isLoading} onClick={onClickDelete}>
            {t('brands.deleteButton')}
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
}

export {
  BrandsDeleteModal,
  SingleContent as BrandsSingleDelete,
  BulkContent as BrandsBulkDelete,
  AllOutletContent as BrandsAllOutletDelete,
};
